import {
  CommonModule,
  HashLocationStrategy,
  LocationStrategy,
  TitleCasePipe,
} from "@angular/common";
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpModule } from "@angular/http";
import { BrowserModule } from "@angular/platform-browser";
import { Network } from "@ionic-native/network";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { CertificationModule } from "certification/certification.module";
import { CertificationService } from "certification/services/certification-service";
import { BenefitsPopoverComponent } from "components/benefits-popover/benefits-popover";
import { CalendarScheduleOrder } from "components/calendar-schedule-order/calendar-schedule-order";
import { CalendarScheduleOrderModule } from "components/calendar-schedule-order/calendar-schedule-order.module";
import { SignInPage } from "components/cognito/sign-in-cognito/sign-in";
import { ConfigurableCardsModule } from "components/configurable/cards/configurable-cards.module";
import { NewWoundLocationComponent } from "components/configurable/cards/new-wound-site-card/new-wound-location/new-wound-location";
import { NewWoundNotesComponent } from "components/configurable/cards/new-wound-site-card/new-wound-notes/new-wound-notes";
import { NewWoundScoreComponent } from "components/configurable/cards/new-wound-site-card/new-wound-score/new-wound-score";
import { NewWoundSitePopoverPage } from "components/configurable/cards/new-wound-site-card/new-wound-site-popover/new-wound-site-popover";
import { NewWoundSummaryComponent } from "components/configurable/cards/new-wound-site-card/new-wound-summary/new-wound-summary";
import { NewWoundService } from "components/configurable/cards/new-wound-site-card/new-wound.service";
import { SharedEligibilityService } from "components/payers/eligibility/shared-eligibility";
import { WoundHistoryPopoverComponent } from "components/wound-history-popover/wound-history-popover";
import { IonicApp, IonicModule } from "ionic-angular";
import HeaderData from "mocks/mock-header-data";
import { ActiveMedicationsPopover } from "navigation/components/active-medications-popover/active-medications-popover";
import { AlertsPopoverPage } from "navigation/components/alerts-popover/alerts-popover";
import { AllergiesPopover } from "navigation/components/allergies-popover/allergies-popover";
import { AppsPopoverPage } from "navigation/components/apps-popover/apps-popover";
import { CalendarNotificationsPopoverPage } from "navigation/components/calendar-notifications-popover/calendar-notifications-popover";
import { DeclineDashboardPopover } from "navigation/components/decline-dashboard-popover/decline-dashboard-popover";
import { EditViewPopoverPageModule } from "navigation/components/edit-view-popover/edit-view-popover.module";
import { UserPopoverPage } from "navigation/components/user-popover/user-popover";
import { LoggerConfig, LoggerModule } from "ngx-logger";
import { EFaxAssessmentPopupPage } from "pages/assessment/efax-assessment-popup/efax-assessment-popup";
import { EditVisitTimeModule } from "pages/assessment/visits/edit-visit-time/edit-visit-time.module";
import { EncountersPopoverComponent } from "pages/assessment/visits/encounters/encounters-popover";
import { UploadError } from "pages/assessment/visits/upload-error/upload-error";
import { AddClaimNotes } from "pages/billing/claim-management/add-claim-notes/add-claim-notes";
import { FirstTimeLogin } from "pages/common/cognito/first-time-login/first-time-login";
import { FirstTimeLoginCognitoModule } from "pages/common/cognito/first-time-login/first-time-login.module";
import { ForgotPassword } from "pages/common/cognito/forgot-password-cognito/forgot-password";
import { ForgotPasswordCognitoModule } from "pages/common/cognito/forgot-password-cognito/forgot-password.module";
import { ResetPassword } from "pages/common/cognito/reset-password-cognito/reset-password";
import { ResetPasswordCognitoModule } from "pages/common/cognito/reset-password-cognito/reset-password.module";
import { TermsConditions } from "pages/common/cognito/terms-conditions/terms-conditions";
import { TermsConditionsModule } from "pages/common/cognito/terms-conditions/terms-conditions.module";
import { DashboardPage } from "pages/common/dashboard/dashboard";
import { DashboardPageModule } from "pages/common/dashboard/dashboard.module";
import { HomePage } from "pages/common/home/home";
import { CompanyResourcesPage } from "pages/company-resources/company-resources";
import { CompanyResourcesPageModule } from "pages/company-resources/company-resources.module";
import { ClearingHousePageModule } from "pages/company/clearinghouse-view/clearinghouse-view.module";
import { WorkgroupPermissionDetail } from "pages/company/company-details/workgroup-permission-details/workgroup-permission-details";
import { WorkgroupPermissionDetailModule } from "pages/company/company-details/workgroup-permission-details/workgroup-permission-details.module";
import { FacilityModule } from "pages/company/facility/facility.module";
import { LocationGroupModule } from "pages/company/location-group/location-group.module";
import { EPrescribePageModule } from "pages/eprescribe/eprescribe.module";
import { AddNotesPopupPageModule } from "pages/order-managment/add-notes/add-notes.module";
import { ListOrdersPageModule } from "pages/order-managment/list-orders/list-orders.module";
import { OrderEntryOMPageModule } from "pages/orders-entry-om/orders-entry-om/order-entry-om.module";
import { ProviderPanelPageModule } from "pages/orders-entry-om/provider-panel/provider-panel.module";
import { StandingOrderPageModule } from "pages/orders/standing-orders/standing-orders.module";
import { AllergiesPageModule } from "pages/patient-chart/allergies/allergies.module";
import { BenefitsServiceProvider } from "pages/patient-chart/benefits/benefitsServiceProvider";
import { PatientBiometricsModule } from "pages/patient-chart/biometrics/biometrics.module";
import { DisenrollPopoverPage } from "pages/patient-chart/disenroll-popover/disenroll-popover";
import { Eligibility2PageModal } from "pages/patient-chart/eligibility-2/eligibility-2-modal";
import { EligibilityPageModal } from "pages/patient-chart/eligibility/eligibility-modal";
import { MedicationsPageModule } from "pages/patient-chart/medications/medications.module";
import { PatientEPrescribePageModule } from "pages/patient-chart/patient-eprescribe/patient-eprescribe.module";
import { PatientReportsModule } from "pages/patient-chart/patient-reports/patient-reports.module";
import { ActiveIssuesPage } from "pages/plan-of-care/active-issues/active-issues";
import { DeclinedPage } from "pages/plan-of-care/declined/declined";
import { ResolvedPage } from "pages/plan-of-care/resolved/resolved";
import { SuggestedPage } from "pages/plan-of-care/suggested/suggested";
import { AdmissionHisProvider } from "providers/admission-his/admission-his";
import { AlertsDataService } from "providers/alerts-data-service/alerts-data-service";
import { AnalyticsProvider } from "providers/analytics/analytics";
import { BereavementService } from "providers/bereavement-service/bereavement-service";
import { ManualPaymentDataService } from "providers/billing-data-service/manual-payment-data-service";
import { BillingService } from "providers/billing/billing-service";
import { SnapshotAvailabilityService } from "providers/billing/snapshot-availability.service";
import { CalendarService } from "providers/calendar-service/calendar-service";
import { CareTeamsService } from "providers/care-teams-service/care-teams-service";
import { CommunityBereavementServiceProvider } from "providers/community-bereavement-service/community-bereavement-service";
import { CompanyDataService } from "providers/company-data-service/company-data-service";
import { CompanyManagementService } from "providers/company-management-service/company-management-service";
import { CompanyTeamsService } from "providers/company-teams-service/company-teams-service";
import { ComplianceGapService } from "providers/compliance-gap-service/compliance-gap-service";
import { ContentConfigService } from "providers/content-config/content-config-service";
import { CustomHttpInterceptor } from "providers/custom-http-interceptor/custom-http.interceptor";
import { DeathVisitHistoryService } from "providers/death-visit-history/death-visit-history.service";
import { DictationTranscriptionService } from "providers/dictation-transcription/dictation-transcription.service";
import { DischargeHisProvider } from "providers/discharge-his/discharge-his";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { FacilitiesService } from "providers/facilities-service/facilities.service";
import { FeatureToggleDataService } from "providers/features-service/feature-toggle.data.service";
import { HisDataService } from "providers/his-service/his-data-service";
import { HospiceDataService } from "providers/hospice-data-service/hospice-data-service";
import { IamDataService } from "providers/iam-service/iam.data.service";
import { MarDataServiceProvider } from "providers/mar-data-service/mar-data-service";
import { MasterDataService } from "providers/master-data-service/master-data-service";
import { MobileOfflineDataProvider } from "providers/mobile-offline-data/mobile-offline-data";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { CreateAsmtService } from "providers/offline-asmt-data-service/create-asmt-service";
import { OfflineAsmtDataServiceProvider } from "providers/offline-asmt-data-service/offline-asmt-data-service";
import { MasterDataManager } from "providers/offline-manager/master-data-manager";
import { OfflineRequestProcessor } from "providers/offline-manager/offline-request-processor";
import { PatientDataManager } from "providers/offline-manager/patient-data-manager";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { OrderDataService } from "providers/order-data-service/order-data-service";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { ActivitiesProvider } from "providers/patient-charts/activitiesService";
import { BiometricsService } from "providers/patient-charts/biometrics.service";
import { DiagnosisService } from "providers/patient-charts/diagnosis-service";
import { LevelOfCareProvider } from "providers/patient-charts/levelOfCareService";
import { NotesServiceProvider } from "providers/patient-charts/notesService";
import { PalliativeService } from "providers/patient-charts/palliative-service";
import { PatientAllergiesService } from "providers/patient-charts/patient-allergies-service";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { PatientChartStatusServiceProvider } from "providers/patient-charts/patient-chart-status-service";
import { PatientChartService } from "providers/patient-charts/patient-chart.service";
import { PatientHistoryServiceProvider } from "providers/patient-charts/patient-history-service";
import { PatientMedicationsService } from "providers/patient-charts/patient-medications.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { PayersService } from "providers/payers-service/payers.service";
import { PlanOfCareDataService } from "providers/poc-data-service/poc-data-service";
import { QapiPlusExportDataService } from "providers/qapi-plus-export/qapi-plus-export.data.service";
import { ReportExecutionService } from "providers/report-execution-service/report-execution.service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { SignatureProvider } from "providers/signature-service/signature-service";
import { SpinnerService } from "providers/spinnerService/spinnerService";
import { StateManagementModule } from "providers/state-management/state-management.module";
import { SystemAdminService } from "providers/system-admin/system-admin.service";
import { UserNotificationsService } from "providers/users-service/user-notifications.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { VolunteerManagementServiceProvider } from "providers/volunteer-management-service/volunteer-management-service";
import { SignInCognitoModule } from "../components/cognito/sign-in-cognito/sign-in.module";
import { CommonComponentsModule } from "../components/common/common-components.module";
import { EvaChatModule } from "../components/eva-chat/eva-chat.module";
import { CalendarPopoverComponent } from "../navigation/components/calendar-popover/calendar-popover";
import { NavigationModule } from "../navigation/navigation.module";
import { NavigationService } from "../navigation/navigation.service";
import { BillingPageModule } from "../pages/billing/billing.module";
import { ClaimOptions } from "../pages/billing/claim-management/claim-options/claim-options";
import { CompanyPageModule } from "../pages/company/company.module";
import { PatientBenefitsPageModule } from "../pages/patient-chart/patient-benefits/patient-benefits.module";
import { UserSettingsModule } from "../pages/user-settings/user-settings.module";
import { CredentialedResourcePipe } from "../pipes/output-formatters/credentialed-resource.pipe";
import { AppStatus } from "../providers/app-status/app-status";
import { AlertWebSocketService } from "../providers/appsync/alter-notifications-websocket.service";
import { ConfigurationService } from "../providers/configuration/configuration.service";
import { EthnicityRaceSyncService } from "../providers/ethnicity-race-sync/ethnicity-race-sync.service";
import { EvaService } from "../providers/eva/eva.service";
import { HopeProvider } from "../providers/his-service/hope-provider";
import { MobileUtilsProvider } from "../providers/mobile-utils/mobile-utils";
import { DatabaseProvider } from "../providers/offline-database/db-connection";
import { OnlineStatusModule } from "../providers/online-status/online-status.module";
import { OpenVisitsService } from "../providers/open-visits/open-visits.service";
import { PatientPreVisitSummaryService } from "../providers/patient-charts/patient-pre-visit-summary-service";
import { PrnService } from "../providers/prn-service/prn-service";
import { WebSocketService } from "../providers/prn-service/socket";
import { QaQueueService } from "../providers/qa-queue/qa-queue.service";
import { RolesService } from "../providers/roles/roles.service";
import { StateManagementService } from "../providers/state-management/state-management.service";
import { UserProfileService } from "../providers/user-profile/user-profile.service";
import { UsersService } from "../providers/users-service/users.service";
import { TranslateHttpLoader } from "../utils/custom-translator-loader/";
import { AccessControlService } from "./access-control/access-control-service";
import { MyApp } from "./app.component";
import { AuthService } from "./auth/auth.service";
import { IDPService } from "./auth/idp.service";
import { AuthorizationHttpInterceptor } from "./authorization/AuthorizationHttpInterceptor";
import { CurantisErrorService } from "./curantisErrorService";
import { LocationService } from "./location/location.service";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@NgModule({
  declarations: [
    MyApp,
    HomePage,
    ActiveIssuesPage,
    DeclinedPage,
    ResolvedPage,
    SuggestedPage,
    EligibilityPageModal,
    Eligibility2PageModal,
    DisenrollPopoverPage,
    BenefitsPopoverComponent,
    EncountersPopoverComponent,
    WoundHistoryPopoverComponent,
    EFaxAssessmentPopupPage,
    AllergiesPopover,
    DeclineDashboardPopover,
    ActiveMedicationsPopover,
    ClaimOptions,
    AddClaimNotes,
    UploadError,
  ],
  imports: [
    IonicModule,
    HttpModule,
    BrowserModule,
    // BrowserAnimationsModule,
    HttpClientModule,
    NavigationModule,
    OnlineStatusModule,
    LoggerModule,
    CommonModule,
    CommonComponentsModule,
    NavigationModule,
    // IonicModule.forRoot(ProtectedContent),
    // IonicPageModule.forChild(ProtectedContent),
    // ProtectedContent,
    NgSelectModule,
    StandingOrderPageModule,
    ConfigurableCardsModule,
    CertificationModule,
    CalendarScheduleOrderModule,
    SignInCognitoModule,
    ForgotPasswordCognitoModule,
    ResetPasswordCognitoModule,
    FirstTimeLoginCognitoModule,
    TermsConditionsModule,
    CompanyResourcesPageModule,
    UserSettingsModule,
    // CompanyPageModule,
    LocationGroupModule,
    ClearingHousePageModule,
    WorkgroupPermissionDetailModule,
    FacilityModule,
    // CompanyManagementPageModule,
    EditViewPopoverPageModule,
    StateManagementModule,
    PatientBiometricsModule,
    EditVisitTimeModule,
    DashboardPageModule,
    MedicationsPageModule,
    EPrescribePageModule,
    PatientEPrescribePageModule,
    AllergiesPageModule,
    PatientReportsModule,
    OrderEntryOMPageModule,
    ListOrdersPageModule,
    PatientBenefitsPageModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    IonicModule.forRoot(MyApp, {
      menuType: "reveal",
      platforms: {
        ios: {
          scrollAssist: false,
          autoFocusAssist: false,
        },
        android: {
          scrollAssist: false,
          autoFocusAssist: false,
        },
      },
    }),
    // AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    CommonComponentsModule,
    AddNotesPopupPageModule,
    //AmplifyAuthenticatorModule,
    ProviderPanelPageModule,
    CompanyPageModule,
    BillingPageModule,
    IonicModule,
    EvaChatModule,
  ],
  bootstrap: [IonicApp],
  entryComponents: [
    MyApp,
    HomePage,
    // VisitsPage,
    DashboardPage,
    AppsPopoverPage,
    CalendarPopoverComponent,
    UserPopoverPage,
    CalendarNotificationsPopoverPage,
    AlertsPopoverPage,
    ActiveIssuesPage,
    DeclinedPage,
    ResolvedPage,
    SuggestedPage,
    EligibilityPageModal,
    Eligibility2PageModal,
    DisenrollPopoverPage,
    CompanyResourcesPage,
    CalendarScheduleOrder,
    SignInPage,
    ForgotPassword,
    ResetPassword,
    FirstTimeLogin,
    TermsConditions,
    BenefitsPopoverComponent,
    EncountersPopoverComponent,
    NewWoundLocationComponent,
    NewWoundScoreComponent,
    NewWoundNotesComponent,
    NewWoundSummaryComponent,
    WoundHistoryPopoverComponent,
    NewWoundSummaryComponent,
    NewWoundSitePopoverPage,
    EFaxAssessmentPopupPage,
    WorkgroupPermissionDetail,
    AllergiesPopover,
    DeclineDashboardPopover,
    ActiveMedicationsPopover,
    ClaimOptions,
    AddClaimNotes,
    UploadError,
  ],
  providers: [
    IamDataService,
    FeatureToggleDataService,
    Network,
    AppStatus,
    MobileUtilsProvider,
    DatabaseProvider,
    ConfigurationService,
    StateManagementService,
    NavigationService,
    AuthService,
    IDPService,
    LocationService,
    TitleCasePipe,
    CredentialedResourcePipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationHttpInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initConfig,
      deps: [ConfigurationService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: CurantisErrorService,
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    UsersService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptor,
      multi: true,
    },
    DeathVisitHistoryService,
    ContentConfigService,
    PlanOfCareDataService,
    ManualPaymentDataService,

    AlertsDataService,
    CareTeamsService,
    CompanyTeamsService,
    DocManagementDataService,
    AccessControlService,
    CalendarService,
    OrderDataService,
    HospiceDataService,
    CalendarService,
    AnalyticsProvider,
    CompanyDataService,
    NotesServiceProvider,
    ReportExecutionService,
    ComplianceGapService,
    ActivitiesProvider,
    OfflineRequestProcessor,
    MasterDataService,
    MasterDataManager,
    OfflineAsmtDataServiceProvider,
    MobileOfflineDataProvider,
    PatientDataManager,
    SpinnerService,
    CreateAsmtService,
    ReferralsServiceProvider,
    DiagnosisService,
    BillingService,
    SnapshotAvailabilityService,
    AdmissionHisProvider,
    DischargeHisProvider,
    SignatureProvider,
    PatientChartService,
    PatientChartStatusServiceProvider,
    LevelOfCareProvider,
    FacilitiesService,
    PayersService,
    CompanyManagementService,
    ResourceManagementService,
    PalliativeService,
    MarDataServiceProvider,
    BereavementService,
    NavBarServiceProvider,
    BenefitsServiceProvider,
    UtilityService,
    NewWoundService,
    UserNotificationsService,
    HeaderData,
    CertificationService,
    VolunteerManagementServiceProvider,
    BiometricsService,
    PatientMedicationsService,
    PatientAllergiesService,
    OrderManagementDataService,
    HisDataService,
    HopeProvider,
    QapiPlusExportDataService,
    QaQueueService,
    OpenVisitsService,
    {
      provide: LoggerConfig,
      useFactory: () => ConfigurationService.loadJson<LoggerConfig>(),
    },
    CommunityBereavementServiceProvider,
    PatientHistoryServiceProvider,
    PatientBenefitsService,
    SystemAdminService,
    RolesService,
    UserProfileService,
    MedicationDataService,
    PrnService,
    WebSocketService,
    DictationTranscriptionService,
    AlertWebSocketService,
    SharedEligibilityService,
    EthnicityRaceSyncService,
    PatientPreVisitSummaryService,
    EvaService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}

export function initConfig(appConfigService: ConfigurationService) {
  return () => appConfigService.loadConfig();
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts